import { useEffect, useState } from 'react'

import { feedQuery, type EmbedConfig } from './embedConfig'
import type { EventsFeedPage, PublicEvent } from './publicEvent'

export type FeedStatus = 'loading' | 'ready' | 'error'

const MAX_PAGES = 10

async function fetchAllEvents({ feedUrl, query, signal }: { feedUrl: string; query: string; signal: AbortSignal }) {
  const params = new URLSearchParams(query)
  const events: PublicEvent[] = []
  let total = 0
  let page: number | null = 1
  while (page && page <= MAX_PAGES) {
    params.set('page', String(page))
    const response = await fetch(`${feedUrl}?${params}`, { signal, headers: { Accept: 'application/json' } })
    if (!response.ok) throw new Error(`Events feed returned ${response.status}`)
    const body = (await response.json()) as EventsFeedPage
    events.push(...body.data)
    total = body.meta.total
    page = body.meta.next_page
  }
  return { events, total }
}

export type EventsFeed = { events: PublicEvent[]; total: number; status: FeedStatus; retry: () => void }

type Loaded = { key: string; status: 'ready' | 'error'; events: PublicEvent[]; total: number }

export function useEventsFeed({ feedUrl, config }: { feedUrl: string; config: EmbedConfig }): EventsFeed {
  const [attempt, setAttempt] = useState(0)
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const query = feedQuery(config)
  const key = `${feedUrl}?${query}#${attempt}`

  useEffect(() => {
    const controller = new AbortController()
    fetchAllEvents({ feedUrl, query, signal: controller.signal })
      .then(({ events, total }) => setLoaded({ key, status: 'ready', events, total }))
      .catch(() => {
        if (!controller.signal.aborted) setLoaded({ key, status: 'error', events: [], total: 0 })
      })
    return () => controller.abort()
  }, [feedUrl, query, key])

  const current = loaded?.key === key ? loaded : null
  const status: FeedStatus = current?.status ?? 'loading'

  return {
    events: current?.events ?? loaded?.events ?? [],
    total: current?.total ?? 0,
    status,
    retry: () => setAttempt((count) => count + 1),
  }
}
